import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Paperclip, X, Send } from "lucide-react";
import { SUPPORT_CATEGORY_LABELS } from "@/pages/seller/Support";

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];
const MAX_BYTES = 5 * 1024 * 1024;

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const SupportCreate = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ subject: "", category: "", priority: "medium", description: "", order_number: "", awb: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const ok = picked.filter((f) => {
      if (f.size > MAX_BYTES) { toast.error(`${f.name} exceeds the 5 MB limit`); return false; }
      return true;
    });
    setFiles((prev) => [...prev, ...ok].slice(0, 5));
    e.target.value = "";
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.subject.trim() || !form.category || !form.description.trim()) {
      toast.error("Subject, category and description are required.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("subject", form.subject.trim());
      fd.append("category", form.category);
      fd.append("priority", form.priority);
      fd.append("description", form.description.trim());
      if (form.order_number.trim()) fd.append("order_number", form.order_number.trim());
      if (form.awb.trim()) fd.append("awb", form.awb.trim());
      files.forEach((f) => fd.append("attachments", f));
      const { data } = await axios.post(`${API}/support/tickets`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Ticket ${data.ticket_number} raised`);
      navigate(`/seller/support/${data.id}`);
    } catch (e) {
      toast.error(errDetail(e, "Could not raise the ticket"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full max-w-3xl">
        <PageHeader title="Raise a ticket" subtitle="Describe your issue — our support team will respond here" backTo="/seller/support" />

        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg">Ticket details</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label>Subject <span className="text-red-500">*</span></Label>
              <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Brief summary of the issue" maxLength={140} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Category <span className="text-red-500">*</span></Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SUPPORT_CATEGORY_LABELS).map(([code, label]) => (
                      <SelectItem key={code} value={code}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Description <span className="text-red-500">*</span></Label>
              <Textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Explain what happened, including any relevant details." />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Related order # <span className="text-slate-400">(optional)</span></Label>
                <Input value={form.order_number} onChange={(e) => set("order_number", e.target.value)} placeholder="e.g. EDGB-00012" />
              </div>
              <div className="space-y-1.5">
                <Label>AWB <span className="text-slate-400">(optional)</span></Label>
                <Input value={form.awb} onChange={(e) => set("awb", e.target.value)} placeholder="Tracking / AWB number" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Attachments <span className="text-slate-400">(optional — PDF/JPG/PNG, up to 5 MB each)</span></Label>
              <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 w-fit">
                <Paperclip className="w-4 h-4" /> Add files
                <input type="file" multiple accept=".pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={onFiles} />
              </label>
              {files.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between rounded bg-slate-50 px-3 py-1.5 text-sm">
                      <span className="truncate max-w-[260px]">{f.name}</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => navigate("/seller/support")} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting}>{submitting ? "Submitting…" : <><Send className="w-4 h-4 mr-1.5" /> Raise ticket</>}</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
};

export default SupportCreate;
